import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { founderApi } from '@/lib/founderApi';
import { errMsg } from '@/lib/digitalApi';
import { usePageMeta } from '@/lib/usePageMeta';
import { FounderFrame } from '@/components/founder/FounderFrame';

// Access is never granted here: this page only reports the server-verified order state.
export default function BlueprintThankYou() {
  usePageMeta({ title: 'Thank You', path: '/founder-blueprint-builder/thank-you', robots: 'noindex' });
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ loading: true });
  const [resend, setResend] = useState('');

  useEffect(() => {
    if (!sessionId) { setState({ error: 'Missing checkout session.' }); return undefined; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const r = await founderApi.orderStatus(sessionId);
        setState(r);
        if (r.access_ready || r.access_removed) return;
        if (tries < 10) { tries += 1; timer = setTimeout(poll, 3000); } else setState({ ...r, timedOut: true });
      } catch {
        setState({ error: 'We could not find this checkout session.' });
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const resendLink = async () => {
    try { setResend((await founderApi.resendForSession(sessionId)).message); } catch (e) { setResend(errMsg(e)); }
  };

  return (
    <FounderFrame eyebrow="ORIGENNE™ · Founder Blueprint Builder™" testid="blueprint-thank-you-page">
      {state.error && <p className="text-matte/70" data-testid="blueprint-thank-you-error">{state.error}</p>}
      {state.access_removed && (
        <p className="text-matte/70 leading-relaxed" data-testid="blueprint-thank-you-removed">
          Access for this purchase is no longer active. If you believe this is a mistake, please write to iamtoiawallace@gmail.com.
        </p>
      )}
      {state.timedOut && !state.access_ready && (
        <div data-testid="blueprint-thank-you-timeout">
          <h1 className="font-serif-display text-5xl md:text-6xl tracking-tighter text-matte">Still confirming&hellip;</h1>
          <p className="mt-6 text-matte/70 leading-relaxed">
            Your payment is taking longer than usual to confirm. Once it&rsquo;s verified, your sign-in link will be emailed to you.
            You can also try signing in shortly, or write to iamtoiawallace@gmail.com for help.
          </p>
          <Link to="/founder/login" className="btn-primary mt-10" data-testid="blueprint-thank-you-timeout-login">Go to Sign In</Link>
        </div>
      )}
      {!state.error && !state.access_ready && !state.access_removed && !state.timedOut && (
        <>
          <h1 className="font-serif-display text-5xl md:text-6xl tracking-tighter text-matte">Confirming your payment&hellip;</h1>
          <p className="mt-6 text-matte/70 flex items-center gap-3" data-testid="blueprint-thank-you-pending">
            <Loader2 size={16} className="animate-spin" /> We verify every payment directly with Stripe. This usually takes a few seconds.
          </p>
        </>
      )}
      {state.access_ready && (
        <>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.98] tracking-tightest text-matte">
            Thank you. <em className="italic text-taupe">Your Workspace is ready.</em>
          </h1>
          <p className="mt-8 text-matte/70 leading-relaxed" data-testid="blueprint-thank-you-ready">
            Your payment is confirmed. A secure sign-in link has been sent to {state.email_masked}. It works once and expires in 15 minutes.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <button onClick={resendLink} className="btn-primary" data-testid="blueprint-thank-you-resend-button">Send Me a New Link</button>
            <Link to="/founder/login" className="btn-ghost" data-testid="blueprint-thank-you-login-link">Go to Sign In</Link>
          </div>
          {resend && <p className="mt-4 text-sm text-matte/70" data-testid="blueprint-thank-you-resend-message">{resend}</p>}
        </>
      )}
    </FounderFrame>
  );
}
